import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Plane, Users, Luggage, Phone, Mail, MessageSquare, MapPin, ArrowRight,
  Check, RefreshCw, Play, Clock, Car,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/StatusBadge";
import Countdown from "@/components/Countdown";
import { STEP_META, fmtLocal, fmtTime } from "@/lib/status";
import api from "@/lib/api";

function Row({ icon: Icon, label, value }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="flex items-start gap-2.5 py-1.5">
      <Icon className="h-4 w-4 text-zinc-500 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
        <div className="text-sm text-zinc-200 break-words">{value}</div>
      </div>
    </div>
  );
}

export default function JourneyDetail({ journey, open, onClose }) {
  const qc = useQueryClient();
  const j = journey;

  const refreshMut = useMutation({
    mutationFn: () => api.refreshJourney(j.journeyId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["journeys"] });
      toast.success("Rezervasyon güncellendi");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Güncellenemedi"),
  });

  const stepMut = useMutation({
    mutationFn: (step) => api.manualStep(j.journeyId, step),
    onSuccess: (_, step) => {
      qc.invalidateQueries({ queryKey: ["journeys"] });
      qc.invalidateQueries({ queryKey: ["logs"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      toast.success("Adım gönderildi");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Adım gönderilemedi"),
  });

  const assignMut = useMutation({
    mutationFn: () => api.assignJourney(j.journeyId, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["journeys"] });
      qc.invalidateQueries({ queryKey: ["logs"] });
      toast.success("Sürücü + araç atandı (PLANNED)");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Atama başarısız"),
  });

  if (!j) return null;
  const steps = j.steps || {};
  const schedule = j.schedule || {};

  const route = (loc) =>
    loc?.resolvedAddress || [loc?.establishment, loc?.city].filter(Boolean).join(", ") || "—";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="journey-detail-dialog"
        className="max-w-3xl bg-zinc-950/95 backdrop-blur-xl border-zinc-800 p-0 gap-0 overflow-hidden"
      >
        <DialogHeader className="px-6 py-4 border-b border-zinc-800">
          <div className="flex items-center justify-between gap-3 pr-6">
            <div className="flex items-center gap-3">
              <DialogTitle className="font-mono text-lg">{j.code}</DialogTitle>
              <span className="font-mono text-xs text-blue-400/80">ID: {j.journeyId}</span>
              <StatusBadge status={j.status} />
            </div>
            <DialogDescription className="sr-only">
              {j.code} rezervasyon detayları ve otomasyon adımları
            </DialogDescription>
            <div className="flex items-center gap-2">
              {j.status === "CONFIRMED" && (
                <Button
                  data-testid="detail-assign-btn"
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700 text-white h-8 active:scale-95"
                  onClick={() => assignMut.mutate()}
                  disabled={assignMut.isPending}
                >
                  <Car className="h-3.5 w-3.5 mr-1.5" /> Sürücü + Araç Ata
                </Button>
              )}
              <Button
                data-testid="detail-refresh-btn"
                variant="outline" size="sm"
                className="border-zinc-700 bg-transparent hover:bg-zinc-800 h-8"
                onClick={() => refreshMut.mutate()}
                disabled={refreshMut.isPending}
              >
                <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${refreshMut.isPending ? "animate-spin" : ""}`} /> Yenile
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="grid md:grid-cols-2 max-h-[70vh] overflow-y-auto">
          {/* Left: reservation details */}
          <div className="p-6 border-r border-zinc-800">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Güzergah</div>
            <div className="space-y-2 mb-4">
              <div className="flex items-start gap-2 text-sm">
                <MapPin className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
                <span className="text-zinc-200">{route(j.pickup)}</span>
              </div>
              <div className="flex items-start gap-2 text-sm">
                <ArrowRight className="h-4 w-4 text-blue-400 mt-0.5 shrink-0" />
                <span className="text-zinc-200">{route(j.dropoff)}</span>
              </div>
            </div>

            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1 mt-4">Yolcu Bilgileri</div>
            <div className="divide-y divide-zinc-800/60">
              <Row icon={Users} label="İsim" value={[j.traveller?.firstName, j.traveller?.lastName].filter(Boolean).join(" ")} />
              <Row icon={Phone} label="Telefon" value={j.traveller?.phone} />
              <Row icon={Mail} label="E-posta" value={j.traveller?.email} />
              <Row icon={Plane} label="Uçuş No" value={j.traveller?.flightNumber} />
              <Row icon={Users} label="Yolcu Sayısı" value={j.traveller?.passengerCount} />
              <Row icon={Luggage} label="Bagaj" value={j.traveller?.luggageCount} />
              <Row icon={Car} label="Araç Sınıfı" value={j.vehicleCategory} />
              <Row icon={Clock} label="Alım Saati" value={`${fmtLocal(j.pickupLocalTime)} (${j.pickupTimeZone || ""})`} />
              <Row icon={MessageSquare} label="Sürücü Notu" value={j.traveller?.driverComments} />
            </div>
          </div>

          {/* Right: timeline */}
          <div className="p-6">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-4">Otomasyon Adımları</div>
            <div className="space-y-1">
              {STEP_META.map((s, idx) => {
                const st = steps[s.key] || {};
                const isDone = st.done;
                const isNext = j.nextStep === s.key;
                const err = st.error;
                return (
                  <div key={s.key} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <div
                        className={`h-7 w-7 rounded-full flex items-center justify-center border ${
                          isDone
                            ? "bg-emerald-500/20 border-emerald-500 text-emerald-400"
                            : isNext
                            ? "bg-blue-500/20 border-blue-500 text-blue-400 live-dot"
                            : "bg-zinc-800 border-zinc-700 text-zinc-500"
                        }`}
                      >
                        {isDone ? <Check className="h-4 w-4" /> : <span className="text-xs font-semibold">{idx + 1}</span>}
                      </div>
                      {idx < STEP_META.length - 1 && (
                        <div className={`w-px flex-1 my-1 ${isDone ? "bg-emerald-500/40" : "bg-zinc-800"}`} style={{ minHeight: 28 }} />
                      )}
                    </div>
                    <div className="pb-4 flex-1">
                      <div className="text-sm font-medium text-zinc-200">{s.label}</div>
                      <div className="text-[11px] text-zinc-500">{s.desc}</div>
                      {isDone ? (
                        <div className="text-[11px] font-mono text-emerald-400 mt-1">
                          ✓ {fmtTime(st.at)} {st.source ? `· ${st.source}` : ""}
                        </div>
                      ) : (
                        <div className="text-[11px] font-mono text-zinc-500 mt-1">
                          Planlanan: {fmtTime(schedule[s.key])}
                          {isNext && <Countdown target={j.nextActionAt} className="ml-2 text-[11px]" />}
                        </div>
                      )}
                      {err ? <div className="text-[11px] text-red-400 mt-1">⚠ {err}</div> : null}
                      {isNext && (
                        <Button
                          data-testid={`manual-step-${s.key}`}
                          size="sm"
                          className="mt-2 h-7 bg-blue-600 hover:bg-blue-700 text-white active:scale-95"
                          onClick={() => stepMut.mutate(s.key)}
                          disabled={stepMut.isPending}
                        >
                          <Play className="h-3 w-3 mr-1.5" /> Şimdi Çalıştır
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
